/**
 * The inventory panel.
 *
 * Draws whatever the server last said the player is carrying. Dragging and clicking
 * send intent and nothing more: the panel never decides that an item moved, it draws
 * the answer. A move the server refuses simply arrives back the way it was.
 *
 * Icons are placeholder art. Each item gets a stable colour from its id and its
 * initials, which is enough to tell a hatchet from a fish until real icons exist.
 */

import { useRef, useState } from 'react';
import type { ItemStack } from '@inverse/common';
import type { ItemDefinitionWire } from '@inverse/protocol';
import { useLongPress } from './longPress';
import { initials, shortQuantity, tint } from './slots';

export function Inventory({
  slots,
  wanted,
  using,
  onBind,
  onHover,
  keys,
  describe,
  onUse,
  onMenu,
  onMove,
}: {
  slots: readonly (ItemStack | null)[];
  /**
   * Items the followed quest step is still waiting to be held.
   *
   * The slot holding one of these is lit, so "wield the pickaxe" points at the
   * pickaxe rather than at twenty eight squares that all look alike. Empty
   * whenever no quest is being followed.
   */
  wanted?: ReadonlySet<number>;
  /**
   * The slot picked up to be used on something, or nothing.
   *
   * It stays lit until it lands somewhere or is put down. Half a gesture with
   * nothing on screen to show for it is half a gesture somebody forgets they
   * are in the middle of.
   */
  using?: number | null;
  describe: (itemId: number) => ItemDefinitionWire | undefined;
  /** Left click: the item's first menu entry, exactly as the right click menu lists it. */
  onUse: (slot: number) => void;
  onMenu: (slot: number, screenX: number, screenY: number) => void;
  /** A middle click on a slot, which is how it is given a key. Absent on touch. */
  onBind?: (slot: number) => void;
  /** What the pointer is resting on, so the tooltip above can say what it is. */
  onHover?: (slot: number | null) => void;
  /** What each slot is bound to, shown in its corner. */
  keys?: Readonly<Record<number, string>>;
  onMove: (from: number, to: number) => void;
}): JSX.Element {
  // Which slot a finger went down on. Shared by the hold and the drag, since both
  // start the same way and only diverge once the finger moves or does not.
  const pressed = useRef(-1);
  const [dragging, setDragging] = useState(-1);
  /** Set when a drag moved an item, so the click that follows is not also a use. */
  const dragged = useRef(false);

  const hold = useLongPress((x, y) => {
    if (pressed.current >= 0) onMenu(pressed.current, x, y);
  });

  /** The slot under a point, for a drag that has no drag events to work with. */
  const slotAt = (x: number, y: number): number => {
    const element = document.elementFromPoint(x, y)?.closest('.slot');
    const index = element?.getAttribute('data-slot');
    return index === null || index === undefined ? -1 : Number(index);
  };

  // The heading and the slot count belong to the panel this sits in, which draws
  // the same frame whichever tab is showing.
  return (
    <div className="inventory">
      <div className="inventory-grid">
        {slots.map((stack, slot) => {
          const definition = stack ? describe(stack.id) : undefined;
          const name = definition?.name ?? (stack ? `Item ${stack.id}` : '');
          const asked = stack !== null && wanted?.has(stack.id) === true;
          const held = using === slot;

          return (
            <div
              key={slot}
              className={[
                stack ? 'slot filled' : 'slot',
                asked ? 'wanted' : '',
                held ? 'using' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onPointerEnter={() => onHover?.(stack ? slot : null)}
              onPointerLeave={() => onHover?.(null)}
              draggable={stack !== null}
              onDragStart={(event) => {
                event.dataTransfer.setData('text/plain', String(slot));
                event.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(event) => {
                // Without this the browser refuses the drop entirely.
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(event) => {
                event.preventDefault();
                const from = Number(event.dataTransfer.getData('text/plain'));
                if (Number.isInteger(from)) onMove(from, slot);
              }}
              data-slot={slot}
              data-name={name}
              onAuxClick={(event) => {
                // The middle button is not a click anybody does by accident, which
                // is what makes it the right one for "give this slot a key".
                if (event.button !== 1 || !onBind) return;
                event.preventDefault();
                onBind(slot);
              }}
              onPointerDown={(event) => {
                pressed.current = stack ? slot : -1;
                hold.onPointerDown(event);

                // Touch has no drag events, so a drag is a press that moved.
                if (event.pointerType === 'touch' && stack) setDragging(slot);
              }}
              onPointerMove={hold.onPointerMove}
              onPointerUp={(event) => {
                hold.onPointerUp(event);

                if (event.pointerType === 'touch' && dragging >= 0) {
                  const target = slotAt(event.clientX, event.clientY);
                  if (target >= 0 && target !== dragging && !hold.consumed()) {
                    onMove(dragging, target);
                    dragged.current = true;
                  }
                  setDragging(-1);
                }
              }}
              onPointerCancel={() => {
                hold.onPointerCancel();
                setDragging(-1);
              }}
              onClick={() => {
                // Neither the tap that ends a hold nor the one that ends a drag is
                // also a use. Judged on what happened rather than on how the click
                // was produced, so one from a keyboard still works.
                const afterDrag = dragged.current;
                dragged.current = false;
                if (!stack || hold.consumed() || afterDrag) return;
                onUse(slot);
              }}
              onContextMenu={(event) => {
                event.preventDefault();
                if (stack) onMenu(slot, event.clientX, event.clientY);
              }}
            >
              {stack ? (
                <>
                  <span className="slot-icon" style={{ background: tint(stack.id) }}>
                    {initials(name)}
                  </span>
                  {definition?.stackable || stack.quantity > 1 ? (
                    <span className="slot-quantity">{shortQuantity(stack.quantity)}</span>
                  ) : null}
                </>
              ) : null}

              {keys?.[slot] ? <span className="slot-key">{keys[slot]}</span> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
